import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import {
  LayoutDashboard,
  Package,
  Plus,
  Layers,
  Image,
  Download,
  Crown,
  CreditCard,
  Users,
  Key,
  User,
  Settings,
  Palette,
  Activity,
  LogOut,
  Menu,
  X
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2zc3j2vv_jini%20studio%20logo%20.png";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  
  const isActive = (path) => location.pathname === path 
    ? "bg-indigo-50 text-indigo-700" 
    : "text-neutral-600 hover:bg-neutral-100";
  
  const linkClass = (path) => `flex items-center gap-3 px-4 py-3 rounded-xl font-medium ${isActive(path)}`;
  
  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };
  
  const closeMobileMenu = () => setOpen(false);

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/products", icon: Package, label: "Products" },
    { path: "/new-shoot", icon: Plus, label: "Quick Shoot" },
    { path: "/bulk-upload", icon: Layers, label: "Bulk Upload" },
    { path: "/shoots", icon: Image, label: "My Shoots" },
    { path: "/downloads", icon: Download, label: "Downloads" },
    { path: "/subscription", icon: Crown, label: "Subscription" },
    { path: "/usage", icon: CreditCard, label: "Usage" },
    { path: "/team", icon: Users, label: "Team" },
    { path: "/developer-keys", icon: Key, label: "API Keys" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  const adminItems = [
    { path: "/admin", icon: Settings, label: "Admin" },
    { path: "/admin/models", icon: Users, label: "Models" },
    { path: "/admin/backgrounds", icon: Palette, label: "Backgrounds" },
    { path: "/admin/jobs", icon: Activity, label: "AI Jobs" },
    { path: "/admin/showcase", icon: Image, label: "Showcase" },
  ];

  const mobileNavItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/products", icon: Package, label: "Products" },
    { path: "/new-shoot", icon: Plus, label: "Quick Shoot" },
    { path: "/bulk-upload", icon: Layers, label: "Bulk Upload" },
    { path: "/shoots", icon: Image, label: "My Shoots" },
    { path: "/downloads", icon: Download, label: "Downloads" },
    { path: "/usage", icon: CreditCard, label: "Usage" },
    { path: "/profile", icon: User, label: "Profile" },
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-neutral-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="JiniStudio" className="h-12 w-auto" />
          </Link>
          <button 
            onClick={() => setOpen(!open)} 
            className="p-2 hover:bg-neutral-100 rounded-lg"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-white pt-16">
          <nav className="p-4 space-y-2">
            {mobileNavItems.map(({ path, icon: Icon, label }) => (
              <Link 
                key={path} 
                to={path} 
                onClick={closeMobileMenu} 
                className={linkClass(path)}
              >
                <Icon className="w-5 h-5" />
                {label}
              </Link>
            ))}
            
            {isAdmin && (
              <>
                <div className="border-t border-neutral-100 my-4 pt-4">
                  <p className="px-4 text-xs font-semibold text-neutral-400 uppercase">Admin</p>
                </div>
                {adminItems.map(({ path, icon: Icon, label }) => (
                  <Link 
                    key={path} 
                    to={path} 
                    onClick={closeMobileMenu} 
                    className={linkClass(path)}
                  >
                    <Icon className="w-5 h-5" />
                    {label}
                  </Link>
                ))}
              </>
            )}
            
            <button 
              onClick={handleLogout} 
              className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-red-600 hover:bg-red-50 w-full"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-neutral-100 p-6">
        <Link to="/dashboard" className="flex items-center gap-2 mb-8">
          <img src={LOGO_URL} alt="JiniStudio" className="h-20 w-auto" />
        </Link>
        
        <nav className="space-y-1">
          {navItems.map(({ path, icon: Icon, label }) => (
            <Link key={path} to={path} className={linkClass(path)}>
              <Icon className="w-5 h-5" />
              {label}
            </Link>
          ))}
        </nav>
        
        {isAdmin && (
          <div className="mt-8 pt-8 border-t border-neutral-100">
            <p className="px-4 text-xs font-semibold text-neutral-400 uppercase mb-2">Admin</p>
            <nav className="space-y-1">
              {adminItems.map(({ path, icon: Icon, label }) => (
                <Link key={path} to={path} className={linkClass(path)}>
                  <Icon className="w-5 h-5" />
                  {label}
                </Link>
              ))}
            </nav>
          </div>
        )}
        
        {/* User Profile Section */}
        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-xl mb-3">
            <img 
              src={user?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'U')}&background=4338ca&color=fff`} 
              alt={user?.name || "User"} 
              className="w-10 h-10 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-neutral-900 text-sm truncate">{user?.name}</p>
              <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={handleLogout} 
            className="w-full justify-start text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
